import React, { useCallback, useEffect, useState } from 'react';
import { Box, Chip, CircularProgress, Paper, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { documentAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useModules } from '../../contexts/ModuleContext';
import {
  type VehicleComplianceRow,
  type VehicleComplianceSummary,
  formatDocExpiry,
  getExpiryStatusMeta,
} from '../../constants/documentTypes';
import { brandColors, RADIUS } from '../../theme/appTheme';

const STATUS_CARDS: Array<{
  key: keyof Pick<VehicleComplianceSummary, 'expired' | 'expiring_7' | 'expiring_30' | 'valid'>;
  status: string;
  emoji: string;
  label: string;
}> = [
  { key: 'expired', status: 'expired', emoji: '🔴', label: 'Expired' },
  { key: 'expiring_7', status: 'expiring_7', emoji: '🟠', label: 'Expiring within 7 days' },
  { key: 'expiring_30', status: 'expiring_30', emoji: '🟡', label: 'Expiring within 30 days' },
  { key: 'valid', status: 'valid', emoji: '🟢', label: 'Valid' },
];

export const VehicleDocumentsWidget: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { isModuleEnabled } = useModules();
  const [summary, setSummary] = useState<VehicleComplianceSummary | null>(null);
  const [vehicles, setVehicles] = useState<VehicleComplianceRow[]>([]);
  const [loading, setLoading] = useState(true);

  const canView = hasPermission('documents', 'view') && isModuleEnabled('documents');

  const load = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      const res = await documentAPI.getVehicleCompliance({ status: 'alerts' });
      setSummary(res.data?.summary || null);
      setVehicles((res.data?.vehicles || []).slice(0, 8));
    } catch {
      setSummary(null);
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => { load(); }, [load]);

  if (!canView) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 2,
        border: `1px solid ${brandColors.border}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography fontWeight={800} fontSize={15}>Vehicle documents</Typography>
        <Chip
          label="View all"
          size="small"
          clickable
          onClick={() => navigate('/vehicles/documents')}
          sx={{ fontWeight: 700, height: 24 }}
        />
      </Box>

      {loading && !summary ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: 1,
            mb: 1.5,
          }}>
            {STATUS_CARDS.map((card) => {
              const meta = getExpiryStatusMeta(card.status);
              const value = summary?.[card.key] ?? 0;
              return (
                <Paper
                  key={card.key}
                  elevation={0}
                  onClick={() => navigate(`/vehicles/documents?status=${card.status}`)}
                  sx={{
                    p: 1.25,
                    borderRadius: `${RADIUS}px`,
                    border: `1px solid ${meta.border}`,
                    bgcolor: meta.bg,
                    cursor: 'pointer',
                  }}
                >
                  <Typography fontSize={12} fontWeight={700} sx={{ color: meta.color }}>
                    {card.emoji} {card.label}
                  </Typography>
                  <Typography fontWeight={800} fontSize={22} sx={{ color: meta.color, lineHeight: 1.2 }}>
                    {value}
                  </Typography>
                </Paper>
              );
            })}
          </Box>

          {vehicles.length === 0 ? (
            <Typography fontSize={13} color="text.secondary">
              No expired or upcoming vehicle document alerts.
            </Typography>
          ) : (
            <Box sx={{ display: 'grid', gap: 1 }}>
              {vehicles.map((v) => (
                <Paper
                  key={v.vehicleId}
                  elevation={0}
                  onClick={() => navigate(`/vehicles/${v.vehicleId}`)}
                  sx={{
                    p: 1.25,
                    borderRadius: `${RADIUS}px`,
                    border: `1px solid ${brandColors.border}`,
                    cursor: 'pointer',
                    '&:hover': { borderColor: brandColors.primary },
                  }}
                >
                  <Typography fontWeight={800} fontSize={13} sx={{ mb: 0.5 }}>
                    Vehicle: {v.vehicleNumber}
                  </Typography>
                  {v.alerts.map((alert) => {
                    const meta = getExpiryStatusMeta(alert.status);
                    return (
                      <Typography key={`${alert.documentType}-${alert.documentId}`} fontSize={12.5} sx={{ color: meta.color, fontWeight: 600 }}>
                        {meta.emoji} {alert.label} expires: {alert.expiryLabel || formatDocExpiry(alert.expiryDate)}
                      </Typography>
                    );
                  })}
                </Paper>
              ))}
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};
